import dayjs from "dayjs";
import type { IntlShape } from "react-intl";
import {
  z,
  coerce,
  date,
  discriminatedUnion,
  nativeEnum,
  number,
  object,
} from "zod";

import { CabinClass, TripType } from "@/features/flights/types/types";
import { airportSchema, citySchema } from "@/utils/locations";

export const flightFormLocationSchema = discriminatedUnion("type", [
  airportSchema,
  citySchema,
]);

export const flightSearchFormInputSchema = object({
  tripType: nativeEnum(TripType),
  from: flightFormLocationSchema.nullable(),
  to: flightFormLocationSchema.nullable(),
  dates: object({
    departure: coerce.date(),
    return: coerce.date().optional(),
  }),
  adult: number().min(1).max(9),
  child: number().min(0).max(9),
  infantWithoutSeat: number().min(0).max(9),
  cabinClass: nativeEnum(CabinClass),
});
export type FlightSearchFormInputValues = z.infer<
  typeof flightSearchFormInputSchema
>;

export const getFlightSearchFormSchema = (intl: IntlShape) =>
  object({
    dates: object({
      departure: date({
        required_error: intl.formatMessage({
          defaultMessage: "Please select departure date",
          description: "flights.validation.dates.departure",
        }),
      }).refine(
        (date) => {
          if (!date) return true;
          return !dayjs(date).endOf("day").isBefore(dayjs(), "day");
        },
        intl.formatMessage({
          defaultMessage: "Departure date cannot be set before today",
          description: "flights.validation.dates.minDeparture",
        }),
      ),
      return: date().optional(),
    }),
    from: flightFormLocationSchema.nullable().refine(
      (value) => value !== null,
      intl.formatMessage({
        defaultMessage: "Please select departure airport",
        description: "flights.validation.from",
      }),
    ),
    to: flightFormLocationSchema.nullable().refine(
      (value) => value !== null,
      intl.formatMessage({
        defaultMessage: "Please select destination airport",
        description: "flights.validation.to",
      }),
    ),
    tripType: nativeEnum(TripType, {
      required_error: intl.formatMessage({
        defaultMessage: "Please select trip type",
        description: "flights.validation.tripType",
      }),
    }),
    cabinClass: nativeEnum(CabinClass, {
      required_error: intl.formatMessage({
        defaultMessage: "Please select cabin class",
        description: "flights.validation.cabinClass",
      }),
    }),
    adult: number().min(1).max(9),
    child: number().min(0).max(9),
    infantWithoutSeat: number().min(0).max(9),
  }).superRefine((values, ctx) => {
    const { dates, tripType } = values;
    const { departure: departureDate, return: returnDate } = dates;

    if (tripType === TripType.ROUND_TRIP && !returnDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: intl.formatMessage({
          defaultMessage: "Please select return date",
          description: "flights.validation.dates.return",
        }),
      });
    }

    if (returnDate && departureDate && returnDate < departureDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: intl.formatMessage({
          defaultMessage: "Return date cannot be set before departure",
          description: "flights.validation.dates.minReturn",
        }),
      });
    }
  });

export type FlightSearchFormValues = z.infer<
  ReturnType<typeof getFlightSearchFormSchema>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typeCheck: z.input<ReturnType<typeof getFlightSearchFormSchema>> =
  {} as FlightSearchFormInputValues;
